import styled, { css } from 'styled-components';

import { FormFactors } from '#/service/constants';
import { remify } from '#/service/utils';

import { lineClamp } from '../LineClampText';
import {
  hoverOpacity,
  slideFadeInBottomAnimation,
  slideFadeInRightAnimation,
  slideFadeOutBottomAnimation,
  slideFadeOutRightAnimation,
} from '../styles';

const themeFallbackValues = {
  snackbarBackground: '#FDFDFD',
  snackbarForeground: '#0B0B0B',
  snackbarActionForeground: '#0064FF',
};

export const ANIMATION_DURATION = 300;

// --- Style Utilities ---
const shadow = '0px 4px 4px 0px rgba(0, 0, 0, 0.25);';

export const getPositionStyles = (formFactor: FormFactors) => {
  switch (formFactor) {
    case FormFactors.MOBILE:
      return css`
        left: ${remify(16)};
        right: ${remify(16)};
        bottom: ${remify(16)};
        width: auto;
        max-width: calc(100vw - 32px);
        min-width: 0;
        margin: 0;
      `;
    case FormFactors.TABLET:
      return css`
        top: ${remify(120)};
        right: ${remify(32)};
        min-width: 250px;
        max-width: 600px;
        margin: 0;
      `;
    case FormFactors.DESKTOP:
    default:
      return css`
        top: ${remify(90)};
        right: ${remify(50)};
        min-width: 250px;
        max-width: 600px;
        margin: 0;
      `;
  }
};

export const getAnimation = (formFactor: FormFactors) => {
  if (formFactor === FormFactors.MOBILE) {
    return {
      enter: slideFadeInBottomAnimation,
      exit: slideFadeOutBottomAnimation,
    };
  }

  return {
    enter: slideFadeInRightAnimation,
    exit: slideFadeOutRightAnimation,
  };
};

// --- Styled Components ---
export const SnackbarContainer = styled.div<{
  $formFactor: FormFactors;
  $hasAction: boolean;
}>`
  position: fixed;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: ${({ $hasAction, $formFactor }) =>
    $hasAction || $formFactor === FormFactors.MOBILE ? 'flex-start' : 'center'};
  box-shadow: ${shadow};
  background: ${({ theme }) =>
    theme.color.snackbarBackground ?? themeFallbackValues.snackbarBackground};
  border-radius: 5px;
  padding: ${remify(16)} ${remify(24)};
  font: var(--fontStyleBody);
  gap: ${remify(16)};
  ${(props) => getPositionStyles(props.$formFactor)}
  transition: box-shadow 0.2s;
  pointer-events: auto;
  min-height: ${remify(48)};
  max-height: ${remify(200)};
  box-sizing: border-box;

  &.snackbar-enter {
    animation: ${({ $formFactor }) => getAnimation($formFactor).enter}
      ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  &.snackbar-exit {
    animation: ${({ $formFactor }) => getAnimation($formFactor).exit}
      ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
`;

export const SnackbarText = styled.span<{
  $centered: boolean;
}>`
  flex: 1 1 0;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  align-self: center;
  color: ${({ theme }) =>
    theme.color.snackbarForeground ?? themeFallbackValues.snackbarForeground};
  ${lineClamp(2)}
`;

export const SnackbarAction = styled.button`
  background: none;
  border: none;
  font: var(--fontStyleCallout);
  font-weight: 600;
  cursor: pointer;
  margin-left: ${remify(16)};
  padding: 0 ${remify(8)};
  align-self: center;
  text-align: right;
  transition: opacity 0.2s;
  color: ${({ theme }) =>
    theme.color.snackbarActionForeground ??
    themeFallbackValues.snackbarActionForeground};

  ${hoverOpacity}

  &:focus {
    opacity: 0.7;
  }
`;
